import React, { useState, useEffect } from 'react';
import pb from '../pocketbase';
import { FaPaperPlane, FaUser, FaEnvelope, FaPhone, FaWhatsapp, FaInfoCircle, FaCheckCircle, FaTrash } from 'react-icons/fa';
import './AdminChat.css';

const AdminChat = () => {
    const [activeTab, setActiveTab] = useState('contact'); // 'contact' or 'direct'
    const [conversations, setConversations] = useState([]); // Users for direct chat
    const [contactMessages, setContactMessages] = useState([]); // Form messages
    const [selectedItem, setSelectedItem] = useState(null); // Selected user or contact message
    const [messages, setMessages] = useState([]); // Direct chat messages
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (activeTab === 'direct') {
            loadConversations();
        } else {
            loadContactMessages();
        }

        // Real-time subscription
        pb.collection('messages').subscribe('*', (e) => {
            if (activeTab === 'direct') {
                loadConversations();
                if (selectedItem && (e.record.expediteur === selectedItem.id || e.record.destinataire === selectedItem.id)) {
                    selectDirectUser(selectedItem);
                }
            } else {
                loadContactMessages();
            }
        });

        return () => pb.collection('messages').unsubscribe('*');
    }, [activeTab, selectedItem?.id]);

    const loadConversations = async () => {
        try {
            const users = await pb.collection('users').getList(1, 100, { 
                filter: 'role != "admin"',
                sort: '-created',
                requestKey: null
            });
            
            // Get unread counts
            const adminId = pb.authStore.model.id;
            const messages = await pb.collection('messages').getList(1, 400, {
                filter: `lu = false && type != "contact" && destinataire = "${adminId}"`,
                requestKey: null
            });

            const enrichedUsers = users.items.map(u => ({
                ...u,
                unreadCount: messages.items.filter(m => m.expediteur === u.id).length
            }));

            setConversations(enrichedUsers);
        } catch (err) { console.error(err); }
    };

    const loadContactMessages = async () => {
        try {
            const records = await pb.collection('messages').getList(1, 150, {
                filter: 'type = "contact"',
                sort: '-created',
                requestKey: null
            });
            setContactMessages(records.items);
        } catch (err) { console.error(err); }
    };

    const selectDirectUser = async (user) => {
        setSelectedItem(user);
        setLoading(true);
        try {
            const msgs = await pb.collection('messages').getList(1, 100, {
                filter: `(expediteur = "${user.id}" || destinataire = "${user.id}") && type != "contact"`,
                sort: 'created',
                requestKey: null
            });
            setMessages(msgs.items);
            
            // Mark unread messages from this user as read
            const unread = msgs.items.filter(m => m.expediteur === user.id && !m.lu);
            for (const m of unread) {
                await pb.collection('messages').update(m.id, { lu: true });
            }
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    const selectContactMessage = async (msg) => {
        setSelectedItem(msg);
        if (!msg.lu) {
            try {
                await pb.collection('messages').update(msg.id, { lu: true });
                setContactMessages(prev => prev.map(m => m.id === msg.id ? { ...m, lu: true } : m));
            } catch (err) { console.error(err); }
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedItem || activeTab !== 'direct') return;

        const adminId = pb.authStore.model.id;
        try {
            await pb.collection('messages').create({
                contenu: newMessage,
                expediteur: adminId,
                destinataire: selectedItem.id,
                lu: false,
                type: 'chat'
            });
            setNewMessage('');
            selectDirectUser(selectedItem);
        } catch (err) { alert("Erreur envoi"); }
    };

    const deleteMessage = async (id) => {
        if (!window.confirm("Supprimer ce message ?")) return;
        try {
            await pb.collection('messages').delete(id);
            setContactMessages(prev => prev.filter(m => m.id !== id));
            setSelectedItem(null);
        } catch (err) { alert("Erreur suppression"); }
    };

    const sendWhatsApp = (tel) => {
        const cleanTel = tel.replace(/\s/g, '');
        window.open(`https://wa.me/${cleanTel}`, '_blank');
    };

    return (
        <div className="admin-section chat-section fade-in">
            {/* Sidebar List */}
            <div className="chat-sidebar">
                <div className="sidebar-tabs-unified">
                    <button 
                        className={`tab-toggle ${activeTab === 'contact' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('contact'); setSelectedItem(null); }}
                    >
                        Contacts
                        {contactMessages.some(m => !m.lu) && <span className="unread-dot"></span>}
                    </button>
                    <button 
                        className={`tab-toggle ${activeTab === 'direct' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('direct'); setSelectedItem(null); setMessages([]); }}
                    >
                        Discussions
                        {conversations.some(u => u.unreadCount > 0) && <span className="unread-dot"></span>}
                    </button>
                </div>

                <div className="sidebar-list">
                    {activeTab === 'direct' ? (
                        conversations.map(u => (
                            <div
                                key={u.id}
                                onClick={() => selectDirectUser(u)}
                                className={`user-list-item ${selectedItem?.id === u.id ? 'active' : ''}`}
                            >
                                <div className="item-main-info">
                                    <strong>{u.name || u.email}</strong>
                                    <small>{u.email}</small>
                                </div>
                            </div>
                        ))
                    ) : (
                        contactMessages.map(m => (
                            <div
                                key={m.id}
                                onClick={() => selectContactMessage(m)}
                                className={`user-list-item contact-item ${selectedItem?.id === m.id ? 'active' : ''} ${!m.lu ? 'is-new' : ''}`}
                            >
                                <div className="item-main-info">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <strong>{m.nom}</strong>
                                        <small>{new Date(m.created).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</small>
                                    </div>
                                    <p className="msg-preview">{m.contenu?.substring(0, 40)}...</p>
                                </div>
                                {!m.lu && <span className="new-badge">Nouveau</span>}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Content Area */}
            <div className="chat-area">
                {selectedItem ? (
                    activeTab === 'direct' ? (
                        <>
                            <div className="chat-header">
                                <strong>Discussion avec {selectedItem.name || selectedItem.email}</strong>
                            </div>
                            <div className="messages-list">
                                {messages.map(m => {
                                    const isMe = m.expediteur === pb.authStore.model.id;
                                    return (
                                        <div key={m.id} className={`message-wrapper ${isMe ? 'me' : 'client'}`}>
                                            <div className="message-bubble">{m.contenu}</div>
                                            <small className="message-time">
                                                {new Date(m.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </small>
                                        </div>
                                    );
                                })}
                                {messages.length === 0 && !loading && <p className="no-messages">Aucun message.</p>}
                                {loading && <p className="no-messages">Chargement...</p>}
                            </div>
                            <form onSubmit={sendMessage} className="chat-form">
                                <input
                                    className="chat-input"
                                    value={newMessage}
                                    onChange={e => setNewMessage(e.target.value)}
                                    placeholder="Répondre au client..."
                                />
                                <button type="submit" className="chat-send-btn"><FaPaperPlane /></button>
                            </form>
                        </>
                    ) : (
                        <div className="contact-detail-view fade-in">
                            <div className="msg-detail-header">
                                <div className="contact-meta">
                                    <span className="premium-label-small">FORMULAIRE DE CONTACT</span>
                                    <h2>{selectedItem.nom}</h2>
                                    <p className="meta-date">Reçu le {new Date(selectedItem.created).toLocaleString('fr-FR')}</p>
                                </div>
                                <div className="msg-header-actions">
                                    <button className="btn-delete-msg" onClick={() => deleteMessage(selectedItem.id)} title="Supprimer">
                                        <FaTrash />
                                    </button>
                                    <div className="msg-status-icon">
                                        {selectedItem.lu ? <FaCheckCircle style={{ color: '#10b981' }} /> : <FaInfoCircle style={{ color: '#3b82f6' }} />}
                                    </div>
                                </div>
                            </div>

                            <div className="contact-info-grid">
                                <div className="info-item">
                                    <label><FaEnvelope /> Email</label>
                                    <a href={`mailto:${selectedItem.email}`}>{selectedItem.email}</a>
                                </div>
                                <div className="info-item">
                                    <label><FaPhone /> Téléphone</label>
                                    <a href={`tel:${selectedItem.telephone}`}>{selectedItem.telephone}</a>
                                </div>
                            </div>

                            <div className="message-content-box">
                                <label>Message :</label>
                                <div className="content-text">
                                    {selectedItem.contenu}
                                </div>
                            </div>

                            <div className="contact-actions-bar">
                                <button className="btn-contact-action whatsapp" onClick={() => sendWhatsApp(selectedItem.telephone)}>
                                    <FaWhatsapp /> Contacter via WhatsApp
                                </button>
                                <a href={`mailto:${selectedItem.email}`} className="btn-contact-action email">
                                    <FaEnvelope /> Répondre par Email
                                </a>
                                <a href={`tel:${selectedItem.telephone}`} className="btn-contact-action phone">
                                    <FaPhone /> Appeler
                                </a>
                            </div>
                        </div>
                    )
                ) : (
                    <div className="empty-chat-state">
                        <div className="empty-box">
                            <FaEnvelope size={40} style={{ opacity: 0.1, marginBottom: '20px' }} />
                            <p>Sélectionnez un message ou une discussion pour commencer.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminChat;
